import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import {
  Container, Button, Accordion, AccordionDetails, AccordionSummary, Skeleton, Typography, TextField,
} from '@mui/material';
import { useCatch, useEffectAsync } from '../../reactHelper';
import { useTranslation } from '../../common/components/LocalizationProvider';
import PageLayout from '../../common/components/PageLayout';
import { dateToDate, dateToDateLocal } from '../../common/util/converter';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
  },
  buttons: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-evenly',
    '& > *': {
      flexBasis: '33%',
    },
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

const EditItemView = ({
  children,
  endpoint,
  item,
  setItem,
  defaultItem,
  validate,
  onItemSaved,
  menu,
  breadcrumbs,
  customHandle,
  customText,
  noCancel,
  convertDatesToDateLocal,
  setInitEnd = () => {},
  convertDatesToDate,
  getAttractions,
  setAttractions,
  getContents,
  setContents,
  getTags,
  setTags,
}) => {
  const navigate = useNavigate();
  const classes = useStyles();
  const t = useTranslation();

  const { id } = useParams();

  useEffectAsync(async () => {
    if (!item) {
      if (id) {
        const response = await fetch(`/api/${endpoint}/${id}`);
        if (response.ok) {
          if (convertDatesToDateLocal) {
            const res = await response.json();
            Object.keys(res).forEach((key) => {
              if (key.includes('date')) {
                res[key] = dateToDateLocal(res[key]);
              }
            });
            setItem(res);
          } else if (convertDatesToDate) {
            const res = await response.json();
            Object.keys(res).forEach((key) => {
              if (key.includes('date')) {
                res[key] = dateToDate(res[key]);
              }
            });
            setItem(res);
          } else {
            const res = await response.json();
            if (getAttractions) {
              setAttractions(res.attractions);
            }
            if (getContents) {
              setContents(res.contents);
            }
            if (getTags) {
              setTags(res.tags);
            }
            setItem(res);
          }
        } else {
          throw Error(await response.text());
        }
      } else {
        setItem(defaultItem || {});
      }
      setInitEnd(true);
    }
  }, [id, item, defaultItem]);

  const handleSave = useCatch(async () => {
    let url = `/api/${endpoint}`;
    if (id) {
      url += `/${id}`;
    }

    const response = await fetch(url, {
      method: !id ? 'POST' : 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item),
    });

    if (response.ok) {
      if (onItemSaved) {
        onItemSaved(await response.json());
      }
      navigate(-1);
    } else {
      throw Error(await response.text());
    }
  });

  return (
    <PageLayout menu={menu} breadcrumbs={breadcrumbs} noBg normalBack>
      <Container maxWidth="xs" className={classes.container}>
        {item ? children : (
          <Accordion defaultExpanded>
            <AccordionSummary>
              <Typography variant="subtitle1">
                <Skeleton width="10em" />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {[...Array(3)].map((_, i) => (
                <Skeleton key={-i} width="100%">
                  <TextField />
                </Skeleton>
              ))}
            </AccordionDetails>
          </Accordion>
        )}
        <div className={classes.buttons}>
          {!noCancel && (
            <Button
              type="button"
              color="primary"
              variant="outlined"
              onClick={() => navigate(-1)}
              disabled={!item}
            >
              {t('sharedCancel')}
            </Button>
          )}
          <Button
            type="button"
            color="primary"
            variant="contained"
            onClick={!customHandle ? handleSave : customHandle}
            disabled={!item || !validate()}
          >
            {t(customHandle ? customText : 'sharedSave')}
          </Button>
        </div>
      </Container>
    </PageLayout>
  );
};

export default EditItemView;
